import { useContext, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import Cookies from "js-cookie"
import SearchIcon from "@mui/icons-material/Search"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import PaymentIcon from "@mui/icons-material/Payment"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import AddIcon from "@mui/icons-material/Add"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import DeleteIcon from "@mui/icons-material/Delete"
import CloseIcon from "@mui/icons-material/Close"
import SendIcon from "@mui/icons-material/Send"
import InfoIcon from "@mui/icons-material/Info"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"

import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"
import ModalErrorFacturacion from "./ModalErrorFacturacion.js"

const TIPOS_FACTURA_HABILITADOS = [1, 2, 3]

const OPCIONES_IVA = [
  { label: "0%", value: 0 },
  { label: "10.5%", value: 10.5 },
  { label: "21%", value: 21 },
]

const REQUISITOS_TIPO_FACTURA = {
  1: {
    titulo: "Factura A",
    requisitos: [
      "El cliente debe tener CUIT (11 dígitos).",
      "Se usa para ventas a Responsables Inscriptos.",
      "Si el documento no tiene exactamente 11 dígitos, la facturación fallará.",
    ],
  },
  2: {
    titulo: "Factura B",
    requisitos: [
      "El cliente puede tener:",
      "• CUIT (11 dígitos) — si es monotributista o responsable inscripto.",
      "• DNI (7 u 8 dígitos) — si es consumidor con identificación.",
      "• Sin documento — se factura como Consumidor Final.",
      "Es la opción más flexible para la mayoría de ventas.",
    ],
  },
  3: {
    titulo: "Factura C",
    requisitos: [
      "Siempre se factura como Consumidor Final.",
      "No se requiere documento del cliente.",
      "No se discrimina IVA en la factura.",
      "Útil para ventas menores o cuando no se identifica al comprador.",
    ],
  },
}

const formatMontoServicio = (monto) => {
  const n = monto != null ? Number(monto) : NaN
  return Number.isFinite(n) ? `$ ${n.toLocaleString("es-AR")}` : "$ 0"
}

/** Mensaje legible para el modal de error (axios, Error, string). */
function mensajeErrorFacturacionParaUsuario(error) {
  if (error == null) {
    return "No se pudo emitir la factura. Intentá nuevamente en unos minutos."
  }
  if (typeof error === "string") {
    return error.trim() || "No se pudo emitir la factura. Intentá nuevamente."
  }
  const data = error.response?.data
  if (data != null) {
    if (typeof data === "string" && data.trim()) return data.trim()
    const fromObj =
      data.message ||
      data.error ||
      data.msg ||
      (Array.isArray(data.errors) ? data.errors.filter(Boolean).join(". ") : "") ||
      (typeof data.details === "string" ? data.details : "")
    if (fromObj && String(fromObj).trim()) return String(fromObj).trim()
  }
  const m = error.message
  if (m && String(m).trim()) return String(m).trim()
  return "No se pudo emitir la factura. Si el problema continúa, contactá a soporte con la hora del intento."
}

const SectionStep = ({ step, title, highlight = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      py: 0.75,
      bgcolor: (theme) => theme.palette.grey[700],
      borderBottom: 1,
      borderColor: "divider",
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: "white",
        color: "black",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        mr: 1.5,
      }}
    >
      {step}
    </Box>
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{
        textTransform: "uppercase",
        letterSpacing: 1,
        color: "white",
      }}
    >
      {title}
    </Typography>
  </Box>
)

export default function ModalCrearFacturaAfip({ open, onClose, onFacturaCreada }) {
  const {
    getUbicacion,
    ubicaciones,
    getTiposFacturas,
    tiposFacturas,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    getTipoEntidad,
    tipoEntidades,
    tipoMedioDePago,
    getTipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    crearYFacturarVentaManualAfip,
    getAfipSalesPoints,
    user,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  // Receptor
  const [selectedTipoEntidad, setSelectedTipoEntidad] = useState(null)
  const [selectedEntidad, setSelectedEntidad] = useState(null)
  const [entitySearch, setEntitySearch] = useState("")

  // Punto de venta AFIP (reemplaza ubicación del sistema)
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [puntosVentaAfip, setPuntosVentaAfip] = useState([])
  const [selectedPuntoVentaAfip, setSelectedPuntoVentaAfip] = useState(null)
  const [pvLoading, setPvLoading] = useState(false)

  // Servicios adicionales (por el momento única forma de cargar servicios)
  const [serviciosAdicionales, setServiciosAdicionales] = useState([])
  const [nuevaDescripcionAdicional, setNuevaDescripcionAdicional] = useState("")
  /** Valor numérico interno (number o "") para el monto adicional */
  const [nuevoMontoAdicional, setNuevoMontoAdicional] = useState("")
  /** Texto formateado con separadores de miles para el input */
  const [nuevoMontoAdicionalDisplay, setNuevoMontoAdicionalDisplay] = useState("")
  const [nuevoPorcentajeIvaAdicional, setNuevoPorcentajeIvaAdicional] = useState(21)

  // Facturación
  const [selectedTipoFactura, setSelectedTipoFactura] = useState(null)
  const [selectedTipoMedioPago, setSelectedTipoMedioPago] = useState(null)
  const [selectedMedioPago, setSelectedMedioPago] = useState(null)
  const [fechaEmision, setFechaEmision] = useState(dayjs())

  // Monto total (suma de montos de servicios asignados + servicios adicionales)
  const [montoTotal, setMontoTotal] = useState("")
  const [priceError, setPriceError] = useState("")

  // UI
  const [submitLoading, setSubmitLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [openEntidadModal, setOpenEntidadModal] = useState(false)
  const [openErrorFacturacion, setOpenErrorFacturacion] = useState(false)
  const [errorFacturacion, setErrorFacturacion] = useState("")
  const [openAyudaTipoFactura, setOpenAyudaTipoFactura] = useState(false)

  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal],
  )

  const tiposFacturaDisponibles = useMemo(
    () => (tiposFacturas || []).filter((tipo) => TIPOS_FACTURA_HABILITADOS.includes(Number(tipo.id))),
    [tiposFacturas],
  )

  // Solo Cliente (id 1) como opción de tipo entidad (sin Proveedor)
  const tipoEntidadesClienteProveedor = useMemo(
    () => (tipoEntidades || []).filter((t) => Number(t.id) === 1),
    [tipoEntidades],
  )

  const listaEntidadesBusqueda = entidadesClientesTresLetras || []
  const unaSolaEntidadEnResultados =
    listaEntidadesBusqueda.length === 1 && entitySearch.trim().length >= 3

  // Solo ubicaciones con esDestino = false (para asignar a la transacción del sistema)
  const ubicacionesPuntoVenta = useMemo(
    () => (ubicaciones || []).filter((u) => u.esDestino === false),
    [ubicaciones],
  )

  // Carga de puntos de venta AFIP
  const cargarPuntosVentaAfip = async () => {
    setPvLoading(true)
    try {
      const data = await getAfipSalesPoints()
      const activos = (data || []).filter(
        (sp) => sp.Bloqueado === "N" && (!sp.FchBaja || sp.FchBaja === "NULL" || sp.FchBaja === "null")
      )
      setPuntosVentaAfip(activos)
      if (activos.length === 1 && !selectedPuntoVentaAfip) {
        setSelectedPuntoVentaAfip(activos[0])
      }
    } catch (err) {
      console.error("Error cargando puntos de venta AFIP:", err)
    } finally {
      setPvLoading(false)
    }
  }

  // Carga de datos iniciales — solo depende de `open` (las funciones del contexto no son estables)
  useEffect(() => {
    if (!open) return
    getUbicacion()
    getTiposFacturas()
    getTipoEntidad()
    getTipoMedioDePago()
    cargarPuntosVentaAfip()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Poblar automáticamente "Cliente" (id 1) al abrir el modal (no sobrescribir si el usuario ya eligió)
  useEffect(() => {
    if (!open || !tipoEntidadesClienteProveedor.length || selectedTipoEntidad) return
    const cliente = tipoEntidadesClienteProveedor.find((t) => Number(t.id) === 1)
    if (cliente) {
      setSelectedTipoEntidad(cliente)
    }
  }, [open, tipoEntidadesClienteProveedor, selectedTipoEntidad])

  // Búsqueda de entidades por tres letras — solo datos estables como dependencias
  const tipoEntidadId = selectedTipoEntidad?.id ?? null
  useEffect(() => {
    if (!open || entitySearch.trim().length < 3 || !tipoEntidadId) return
    getEntidadesClientesTresLetras(entitySearch, [tipoEntidadId], false, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entitySearch, tipoEntidadId])

  // Una sola coincidencia: dejar fija la entidad sin abrir el desplegable
  useEffect(() => {
    if (!open || entitySearch.trim().length < 3) return
    const list = entidadesClientesTresLetras || []
    if (list.length === 1) {
      setSelectedEntidad(list[0])
    }
  }, [open, entitySearch, entidadesClientesTresLetras])

  // Cascada: tipo medio de pago → medios específicos — solo el ID como dependencia
  const tipoMedioPagoId = selectedTipoMedioPago?.id ?? null
  useEffect(() => {
    if (!tipoMedioPagoId) return
    getMedioDePagoIdTipoEspecifico(selectedTipoMedioPago)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoMedioPagoId])

  // Importe total = solo servicios adicionales (por el momento)
  const sumaAdicionales = useMemo(
    () =>
      serviciosAdicionales.reduce((acc, s) => acc + (Number(s.monto) || 0), 0),
    [serviciosAdicionales],
  )
  const montoTotalCalculado = useMemo(() => {
    if (serviciosAdicionales.length === 0) return ""
    return String(sumaAdicionales)
  }, [serviciosAdicionales.length, sumaAdicionales])

  // Desglose IVA por alícuota (base neta + monto IVA por cada tasa)
  const desgloseIva = useMemo(() => {
    const ivaByPorcentaje = {}
    let subtotalNeto = 0
    for (const s of serviciosAdicionales) {
      const p = Number(s.porcentajeIva ?? 21)
      const precio = Number(s.monto) || 0
      const base = p === 0 ? precio : precio / (1 + p / 100)
      const iva = p === 0 ? 0 : base * (p / 100)
      subtotalNeto += base
      if (p > 0) {
        ivaByPorcentaje[p] = (ivaByPorcentaje[p] || 0) + iva
      }
    }
    return { subtotalNeto, ivaByPorcentaje }
  }, [serviciosAdicionales])

  useEffect(() => {
    setMontoTotal(montoTotalCalculado)
    setPriceError("")
  }, [montoTotalCalculado])

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setSelectedTipoEntidad(null)
      setSelectedEntidad(null)
      setSelectedUbicacion(null)
      setSelectedPuntoVentaAfip(null)
      setServiciosAdicionales([])
      setNuevaDescripcionAdicional("")
      setNuevoMontoAdicional("")
      setNuevoMontoAdicionalDisplay("")
      setNuevoPorcentajeIvaAdicional(21)
      setSelectedTipoFactura(null)
      setSelectedTipoMedioPago(null)
      setSelectedMedioPago(null)
      setFechaEmision(dayjs())
      setMontoTotal("")
      setEntitySearch("")
      setPriceError("")
      setErrorMessage("")
      setSubmitLoading(false)
      setOpenErrorFacturacion(false)
      setErrorFacturacion("")
    }
  }, [open])

  const handleTipoMedioPagoChange = (value) => {
    setSelectedTipoMedioPago(value || null)
    setSelectedMedioPago(null)
  }

  const handleEntidadCreada = (entidadCreada) => {
    setSelectedEntidad(entidadCreada)
    setOpenEntidadModal(false)
  }

  const handleSubmit = async () => {
    setErrorMessage("")

    const tieneServicios = serviciosAdicionales.length > 0
    if (!selectedEntidad || !selectedPuntoVentaAfip || !tieneServicios || !selectedTipoFactura || !selectedMedioPago) {
      setErrorMessage("Completa entidad, punto de venta AFIP, al menos un servicio adicional, tipo de factura y medio de pago.")
      return
    }

    if (priceError || !montoTotal) {
      setErrorMessage(priceError || "No se pudo resolver el importe para esta factura.")
      return
    }

    const idUsuario = Number(user?.id || Cookies.get("idUsuario") || 0)

    if (!idUsuario) {
      setErrorMessage("No se pudo identificar el usuario actual.")
      return
    }

    try {
      setSubmitLoading(true)

      const items = [] // Por el momento solo servicios adicionales

      // Descripción: array JSON con descripcion, monto y porcentajeIva de cada servicio
      const serviciosPayload = serviciosAdicionales.map((s) => ({
        descripcion: s.descripcion,
        monto: Number(s.monto),
        porcentajeIva: Number(s.porcentajeIva ?? 21),
      }))
      const descripcionTransaccion = JSON.stringify(serviciosPayload)

      // idUbicacion para la transacción del sistema (primera ubicación disponible o la seleccionada)
      const idUbicacionSistema = selectedUbicacion?.id || ubicacionesPuntoVenta[0]?.id || 1
      const resultado = await crearYFacturarVentaManualAfip({
        idEntidad: selectedEntidad.id,
        idUbicacion: idUbicacionSistema,
        idMedioDePago: selectedMedioPago.id,
        idTipoFactura: selectedTipoFactura.id,
        items,
        serviciosAdicionales: serviciosPayload,
        descripcionServicio: descripcionTransaccion,
        idUsuario,
        fechaEmision: fechaEmision?.isValid() ? fechaEmision.toDate() : new Date(),
        puntoVentaAfip: selectedPuntoVentaAfip?.Nro,
      })

      if (onFacturaCreada) {
        onFacturaCreada(resultado)
      }

      onClose()
    } catch (error) {
      const msg = mensajeErrorFacturacionParaUsuario(error)
      setErrorFacturacion(msg)
      setOpenErrorFacturacion(true)
      setErrorMessage("")
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <>
      <ModalErrorFacturacion
        open={openErrorFacturacion}
        onClose={() => {
          setOpenErrorFacturacion(false)
          setErrorFacturacion("")
        }}
        errorTecnico={errorFacturacion}
      />

      <Dialog
        open={openAyudaTipoFactura}
        onClose={() => setOpenAyudaTipoFactura(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HelpOutlineIcon color="primary" />
          Requisitos para facturar
        </DialogTitle>
        <DialogContent>
          {selectedTipoFactura && REQUISITOS_TIPO_FACTURA[selectedTipoFactura.id] ? (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: "primary.main" }}>
                {REQUISITOS_TIPO_FACTURA[selectedTipoFactura.id].titulo}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 1 } }}>
                {REQUISITOS_TIPO_FACTURA[selectedTipoFactura.id].requisitos.map((texto, idx) => (
                  <Typography key={idx} component="li" variant="body2" sx={{ color: "text.secondary" }}>
                    {texto}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Seleccioná un tipo de factura para ver qué datos debe cumplir el cliente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAyudaTipoFactura(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={submitLoading ? undefined : onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: (theme) => theme.palette.grey[800],
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ReceiptLongIcon sx={{ fontSize: 28, color: "white" }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
              Crear Factura ARCA
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitLoading} size="small" sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {errorMessage ? <Alert severity="error" sx={{ mb: 1.5 }}>{errorMessage}</Alert> : null}

          {/* Paso 1: Datos del Receptor */}
          <Card variant="outlined" sx={{ borderRadius: 0.75, mb: 2, overflow: "hidden" }}>
            <SectionStep step={1} title="Datos del Receptor" highlight />
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid item xs={12} md={10}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={listaEntidadesBusqueda}
                    value={selectedEntidad}
                    onChange={(_, value) => setSelectedEntidad(value || null)}
                    inputValue={entitySearch}
                    onInputChange={(_, value) => setEntitySearch(value)}
                    disabled={!selectedTipoEntidad}
                    open={unaSolaEntidadEnResultados ? false : undefined}
                    disableClearable={unaSolaEntidadEnResultados}
                    getOptionLabel={(option) =>
                      `${option?.descripcion || ""} ${option?.apellido || ""}`.trim() +
                      (option?.dniCuitCuil ? ` — ${option.dniCuitCuil}` : "")
                    }
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        placeholder="Buscar cliente existente..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon color="action" fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {selectedEntidad?.dniCuitCuil ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Documento: {selectedEntidad.dniCuitCuil}
                    </Typography>
                  ) : null}
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenEntidadModal(true)}
                    sx={{
                      minWidth: 112,
                      alignSelf: { xs: "stretch", md: "flex-start" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    Crear
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Paso 2: Servicios / Productos Adicionales */}
          <Card variant="outlined" sx={{ borderRadius: 0.75, mb: 2, overflow: "hidden" }}>
            <SectionStep step={2} title="Servicios / Productos Adicionales" highlight />
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 1.5,
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: (theme) => theme.palette.grey[100],
                  borderRadius: 1,
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  label="Descripción"
                  placeholder="Ej: Tasa de embarque, propinas, etc."
                  value={nuevaDescripcionAdicional}
                  onChange={(e) => setNuevaDescripcionAdicional(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Monto"
                  type="text"
                  value={nuevoMontoAdicionalDisplay}
                  onChange={(e) => {
                    const input = e.target
                    const inputValue = input.value
                    const cursorPosition = input.selectionStart
                    const { locale, decimalSeparator, thousandSeparator } = localeConfig

                    if (inputValue === "") {
                      setNuevoMontoAdicionalDisplay("")
                      setNuevoMontoAdicional("")
                      return
                    }

                    const parsedValue = parseLocalizedNumber(inputValue, locale)

                    if (parsedValue === null && inputValue.trim() !== "") {
                      return
                    }

                    let formattedValue =
                      parsedValue !== null ? formatInputWithSeparators(parsedValue, locale) : ""

                    const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                    const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                    const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                    if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                      formattedValue += decimalSeparator
                    } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                      const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                      const integerPart = formattedValue.split(decimalSeparator)[0]
                      formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                    }

                    setNuevoMontoAdicionalDisplay(formattedValue)
                    setNuevoMontoAdicional(parsedValue ?? "")

                    setTimeout(() => {
                      const beforeCursor = inputValue.substring(0, cursorPosition)
                      const numericCharsBefore = beforeCursor
                        .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
                        .length

                      let newCursorPosition = formattedValue.length

                      if (numericCharsBefore > 0) {
                        let numericCount = 0
                        for (let i = 0; i < formattedValue.length; i++) {
                          const char = formattedValue[i]
                          if (char !== thousandSeparator) {
                            numericCount++
                            if (numericCount >= numericCharsBefore) {
                              newCursorPosition = i + 1
                              break
                            }
                          }
                        }
                      }

                      input.setSelectionRange(newCursorPosition, newCursorPosition)
                    }, 0)
                  }}
                  onBlur={(e) => {
                    const { locale } = localeConfig
                    const parsed = parseLocalizedNumber(e.target.value, locale)
                    if (parsed !== null && parsed > 0) {
                      const formatted = formatInputWithSeparators(parsed, locale)
                      setNuevoMontoAdicionalDisplay(formatted)
                      setNuevoMontoAdicional(parsed)
                    } else if (e.target.value === "") {
                      setNuevoMontoAdicionalDisplay("")
                      setNuevoMontoAdicional("")
                    }
                  }}
                  placeholder="0,00"
                  sx={{ width: { xs: "100%", md: 140 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "decimal",
                    autoComplete: "off",
                  }}
                />
                <Select
                  size="small"
                  value={nuevoPorcentajeIvaAdicional}
                  onChange={(e) => setNuevoPorcentajeIvaAdicional(Number(e.target.value))}
                  sx={{ width: { xs: "100%", md: 110 } }}
                  displayEmpty
                >
                  {OPCIONES_IVA.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      IVA {op.label}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const desc = (nuevaDescripcionAdicional || "").trim()
                    const { locale } = localeConfig
                    const montoParsed =
                      typeof nuevoMontoAdicional === "number"
                        ? nuevoMontoAdicional
                        : parseLocalizedNumber(nuevoMontoAdicionalDisplay, locale)
                    const monto = montoParsed != null && !Number.isNaN(Number(montoParsed)) ? Number(montoParsed) : 0
                    if (!desc) return
                    setServiciosAdicionales((prev) => [
                      ...prev,
                      {
                        id: `ad-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                        descripcion: desc,
                        monto,
                        porcentajeIva: nuevoPorcentajeIvaAdicional,
                      },
                    ])
                    setNuevaDescripcionAdicional("")
                    setNuevoMontoAdicional("")
                    setNuevoMontoAdicionalDisplay("")
                    setNuevoPorcentajeIvaAdicional(21)
                  }}
                  sx={{
                    minWidth: 112,
                    alignSelf: { xs: "stretch", md: "center" },
                  }}
                >
                  Agregar
                </Button>
              </Box>
              {serviciosAdicionales.length > 0 ? (
                <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: (theme) => theme.palette.grey[100] }}>
                      <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11 }}>
                        Descripción
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, width: 130 }}>
                        Monto
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, width: 90 }}>
                        IVA %
                      </TableCell>
                      <TableCell align="center" sx={{ width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviciosAdicionales.map((s) => (
                      <TableRow key={s.id} sx={{ "&:hover": { bgcolor: "action.hover" } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{s.descripcion}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatMontoServicio(s.monto)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${s.porcentajeIva ?? 21}%`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setServiciosAdicionales((prev) => prev.filter((x) => x.id !== s.id))
                            }
                            sx={{ color: "text.secondary", "&:hover": { color: "error.main", bgcolor: "error.light" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : null}
            </CardContent>
          </Card>

          {/* Paso 3: Datos de Facturación + Resumen */}
          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Card variant="outlined" sx={{ borderRadius: 0.75, overflow: "hidden", height: "100%" }}>
                <SectionStep step={3} title="Datos de Facturación" />
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={puntosVentaAfip}
                        loading={pvLoading}
                        value={selectedPuntoVentaAfip}
                        onChange={(_, value) => setSelectedPuntoVentaAfip(value || null)}
                        getOptionLabel={(option) => `PV ${String(option?.Nro || "").padStart(5, "0")} - ${option?.EmisionTipo || "CAE"}`}
                        isOptionEqualToValue={(opt, val) => opt?.Nro === val?.Nro}
                        renderInput={(params) => (
                          <TextField {...params} label="Punto de venta AFIP" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={tiposFacturaDisponibles}
                          value={selectedTipoFactura}
                          onChange={(_, value) => setSelectedTipoFactura(value || null)}
                          getOptionLabel={(option) => option?.descripcion || ""}
                          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                          renderInput={(params) => (
                            <TextField {...params} label="Tipo de factura" />
                          )}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setOpenAyudaTipoFactura(true)}
                          sx={{
                            color: "primary.main",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                          aria-label="Ayuda tipo de factura"
                        >
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={tipoMedioDePago || []}
                        value={selectedTipoMedioPago}
                        onChange={(_, value) => handleTipoMedioPagoChange(value)}
                        getOptionLabel={(option) => option?.descripcion || ""}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tipo de medio de pago"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PaymentIcon color="action" fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={medioDePagoIdTipoEspecifico || []}
                        value={selectedMedioPago}
                        onChange={(_, value) => setSelectedMedioPago(value || null)}
                        disabled={!selectedTipoMedioPago}
                        getOptionLabel={(option) => option?.descripcion || ""}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Medio de pago"
                            placeholder={!selectedTipoMedioPago ? "Primero seleccione tipo" : ""}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon color="action" fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Fecha de emisión"
                          value={fechaEmision}
                          onChange={(value) => setFechaEmision(value)}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              InputProps: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CalendarMonthIcon color="action" fontSize="small" />
                                  </InputAdornment>
                                ),
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Card
                  sx={{
                    bgcolor: (theme) => theme.palette.grey[700],
                    color: "white",
                    borderRadius: 0.75,
                    overflow: "hidden",
                    border: 1,
                    borderColor: (theme) => theme.palette.grey[600],
                  }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 2,
                        display: "block",
                        mb: 1.5,
                      }}
                    >
                      Resumen de Importes
                    </Typography>
                    <Box sx={{ pb: 1.5, borderBottom: 1, borderColor: (theme) => theme.palette.grey[500] }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                        <Typography variant="body2" sx={{ color: "white" }}>
                          Subtotal (neto)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "white" }}>
                          {formatMontoServicio(desgloseIva.subtotalNeto)}
                        </Typography>
                      </Box>
                      {[21, 10.5].map((p) =>
                        (desgloseIva.ivaByPorcentaje[p] || 0) > 0 ? (
                          <Box
                            key={p}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.75,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", pl: 1 }}>
                              IVA {p}%
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "white" }}>
                              {formatMontoServicio(desgloseIva.ivaByPorcentaje[p])}
                            </Typography>
                          </Box>
                        ) : null
                      )}
                    </Box>
                    <Box sx={{ pt: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "white",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: 2,
                          }}
                        >
                          Importe Total
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "white" }}>
                          {montoTotal ? formatMontoServicio(montoTotal) : "$ 0"}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", fontStyle: "italic", display: "block", mt: 0.75 }}>
                        Valores expresados en Pesos Argentinos (ARS)
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "white",
                    borderRadius: 0.75,
                    border: 1,
                    borderColor: "divider",
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  <InfoIcon sx={{ color: "text.primary", mt: 0.25 }} />
                  <Typography variant="caption" sx={{ color: "black", lineHeight: 1.6 }}>
                    Al emitir la factura, se generará un comprobante electrónico válido ante AFIP y se notificará al cliente.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            bgcolor: (theme) => theme.palette.grey[100],
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={onClose}
            disabled={submitLoading}
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              "&:hover": { bgcolor: (theme) => theme.palette.grey[200] },
            }}
          >
            Cancelar Operación
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{
              px: 3,
              py: 1.5,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 1,
              boxShadow: 2,
            }}
          >
            {submitLoading ? "Facturando..." : "Emitir Factura Electrónica"}
          </Button>
        </Box>
      </Dialog>

      <CrearEntidadClienteModalComponent
        open={openEntidadModal}
        handleClose={() => setOpenEntidadModal(false)}
        entidad={null}
        isEditing={false}
        handleAlerta={() => {}}
        onClienteCreado={handleEntidadCreada}
        tipoEntidad="cliente"
      />
    </>
  )
}
